/**
 * The confirm step before anything that changes what members see: turn on,
 * turn off, publish, restore, discard (spec 2026-10-23 §5: "asks for
 * confirmation with an optional note (goes to the audit log)").
 *
 * The flow copies StageControls.tsx (notifications): a dialog that says what
 * will happen, an optional note, the server's refusal shown INSIDE the dialog
 * so the admin can correct it, and Cancel. It is built on Radix Dialog rather
 * than a hand-made overlay, for what a hand-made one does not give: focus is
 * trapped inside and Escape closes it. Focus goes back to the control that
 * opened it: Radix returns focus only to a Dialog.Trigger, and the callers
 * here open the dialog from many places, so this file remembers the opener.
 * The card constrains its own height (lint
 * admin-modals-constrain-height) so the confirm button stays reachable.
 */
import { useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle } from 'lucide-react';
import type { ConfirmRequest } from '../lib/confirm-requests';

export const NOTE_MAX_CHARS = 500;

export type { ConfirmRequest };

interface Props {
  request: ConfirmRequest | null;
  note: string;
  onNoteChange: (note: string) => void;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  /** Lets the caller move focus after a success; call e.preventDefault() to do so. */
  onCloseAutoFocus?: (e: Event) => void;
}

export function ConfirmDialog({ request, note, onNoteChange, submitting, error, onCancel, onConfirm, onCloseAutoFocus }: Props) {
  const opener = useRef<HTMLElement | null>(null);
  return (
    <Dialog.Root
      open={request !== null}
      onOpenChange={(open) => {
        if (!open && !submitting) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border bg-card text-card-foreground shadow-lg focus:outline-none"
          onOpenAutoFocus={() => {
            opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          }}
          onCloseAutoFocus={(e) => {
            onCloseAutoFocus?.(e);
            if (e.defaultPrevented) return;
            e.preventDefault();
            opener.current?.focus();
          }}
        >
          {request && (
            <>
              <div className="shrink-0 border-b p-6">
                <Dialog.Title className="text-lg font-semibold">{request.title}</Dialog.Title>
              </div>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-6">
                <Dialog.Description asChild>
                  <div className="space-y-2 text-sm">
                    {request.lines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                </Dialog.Description>
                {request.warnings && request.warnings.length > 0 && (
                  <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                    {request.warnings.map((w) => (
                      <li key={w} className="flex gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {request.noteLabel !== null && (
                  <div>
                    <label htmlFor="app-experience-note" className="mb-1 block text-sm font-medium">
                      {request.noteLabel}
                    </label>
                    <textarea
                      id="app-experience-note"
                      value={note}
                      onChange={(e) => onNoteChange(e.target.value)}
                      rows={3}
                      maxLength={NOTE_MAX_CHARS}
                      disabled={submitting}
                      placeholder="For example: New video for the spring campaign."
                      className="w-full rounded-md border bg-background p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">The note goes into the history and the audit log.</p>
                  </div>
                )}
                {error && (
                  <p role="alert" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                    {error}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 justify-end gap-2 border-t p-6">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={submitting}
                  className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={submitting}
                  className={
                    request.destructive
                      ? 'rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'
                      : 'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
                  }
                >
                  {submitting ? 'Saving…' : request.confirmLabel}
                </button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
